import { useState, useRef, useEffect, lazy, Suspense } from "react";
import {
  SlidersHorizontal,
  Search,
  MapPin,
  ShoppingCart,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
const FilterModal = lazy(() => import("./FilterModal"));
const LocationSelectionModal = lazy(() => import("./LocationSelectionModal"));
import DistanceDisplay from "./DistanceDisplay";
const DistanceSelectorSheet = lazy(() => import("./DistanceSelectorSheet"));

interface FeedControlsProps {
  selectedLocation: string;
  activeFilters: { include: string[]; exclude: string[] };
  searchQuery: string;
  selectedExperienceTypes: ("dine-in" | "takeout" | "delivery")[];
  selectedDistance: number | null;
  onLocationChange: (
    name: string,
    lat?: number,
    lng?: number,
    addressId?: number
  ) => void;
  onFiltersChange: (filters: { include: string[]; exclude: string[] }) => void;
  onSearchChange: (query: string) => void;
  onExperienceTypeToggle: (type: "dine-in" | "takeout" | "delivery") => void;
  onDistanceChange: (distance: number | null) => void;
  onOrderNowClick?: () => void;
  cartTotal?: number;
  cartItemCount?: number;
}

export default function FeedControls({
  selectedLocation,
  activeFilters,
  searchQuery,
  selectedExperienceTypes,
  selectedDistance,
  onLocationChange,
  onFiltersChange,
  onSearchChange,
  onExperienceTypeToggle,
  onDistanceChange,
  onOrderNowClick: _onOrderNowClick,
  cartTotal: _cartTotal = 0,
  cartItemCount = 0,
}: FeedControlsProps) {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showDistanceSheet, setShowDistanceSheet] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef(false);

  // Focus search input when it becomes visible
  useEffect(() => {
    if (showSearchInput && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearchInput]);

  // Sync local search with prop
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  // Count active filters
  const activeFilterCount =
    (activeFilters.include?.length || 0) + (activeFilters.exclude?.length || 0);

  const handleSearchClick = () => {
    setShowSearchInput(true);
  };

  const handleSearchClose = () => {
    // Only close the search bar, keep the search term active
    setShowSearchInput(false);
  };

  const handleSearchClear = () => {
    // Clear the search term AND close the search bar
    setShowSearchInput(false);
    setLocalSearchQuery("");
    onSearchChange("");
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    isSubmittingRef.current = true;
    onSearchChange(localSearchQuery);
    // Close search bar on submit (Enter key)
    handleSearchClose();
    // Reset flag after a short delay to allow blur to be ignored
    setTimeout(() => {
      isSubmittingRef.current = false;
    }, 100);
  };

  const handleSearchBlur = () => {
    // Don't close if we just submitted (prevents conflict with Enter key)
    if (isSubmittingRef.current) {
      return;
    }
    // Close search bar when user taps elsewhere or tabs away
    handleSearchClose();
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Tab") {
      // Close search bar on Tab
      handleSearchClose();
    }
  };

  const _experienceOptions = [
    { id: "dine-in", label: "Dine Out", description: "Restaurant dining" },
    { id: "takeout", label: "Takeout", description: "Order & pickup" },
    { id: "delivery", label: "Delivery", description: "Delivered to you" },
  ] as const;

  // Count experience types selected (for badge display)
  const experienceCount = selectedExperienceTypes.length;
  const totalFilterCount = activeFilterCount + (experienceCount < 3 ? 1 : 0); // Show badge if not all experience types selected

  return (
    <div className="border-b border-border bg-background">
      {/* Streamlined Header: Location + Search + Filter */}
      <div className="px-4 py-2.5">
        <div className="flex items-center justify-between gap-2">
          {/* Location Pill + Distance */}
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowLocationModal(true)}
              className="flex items-center gap-1.5 px-3 h-9 rounded-full bg-background border-border"
              data-testid="button-location-selector"
            >
              <MapPin className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium truncate max-w-[120px]">
                {selectedLocation}
              </span>
            </Button>
            <DistanceDisplay
              distance={selectedDistance}
              onClick={() => setShowDistanceSheet(true)}
              className="flex-shrink-0"
            />
          </div>

          {/* Search input - expands when active */}
          {showSearchInput ? (
            <form
              onSubmit={handleSearchSubmit}
              className="flex-1 flex items-center gap-2"
            >
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Search restaurants, dishes..."
                value={localSearchQuery}
                onChange={(e) => {
                  setLocalSearchQuery(e.target.value);
                  onSearchChange(e.target.value);
                }}
                onBlur={handleSearchBlur}
                onKeyDown={handleSearchKeyDown}
                className="flex-1 h-9"
                data-testid="input-search"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleSearchClose}
                className="flex-shrink-0"
                data-testid="button-close-search"
              >
                <X className="w-4 h-4" />
              </Button>
            </form>
          ) : (
            <>
              {/* Active Search Display */}
              {searchQuery && (
                <div className="flex-1 flex items-center gap-2 min-w-0">
                  <Badge
                    variant="secondary"
                    className="flex items-center gap-1.5 px-3 py-1 max-w-full"
                  >
                    <Search className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate text-xs">{searchQuery}</span>
                    <button
                      onClick={handleSearchClear}
                      className="ml-1 hover:text-foreground"
                      data-testid="clear-search"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                </div>
              )}

              {/* Icon Controls */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {/* Search Icon */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleSearchClick}
                  className="h-9 w-9"
                  data-testid="button-open-search"
                >
                  <Search className="w-4 h-4" />
                </Button>

                {/* Filter Icon with Badge */}
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowFilterModal(true)}
                    className="h-9 w-9"
                    data-testid="button-open-filters"
                  >
                    <SlidersHorizontal className="w-4 h-4" />
                  </Button>
                  {totalFilterCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                      data-testid="filter-badge"
                    >
                      {totalFilterCount}
                    </Badge>
                  )}
                </div>

                {/* Cart Display */}
                {cartItemCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 px-2 flex items-center gap-1"
                    data-testid="button-view-cart"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    <span className="text-xs font-medium">{cartItemCount}</span>
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Filter Modal */}
      {showFilterModal && <Suspense fallback={null}><FilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        initialFilters={activeFilters}
        onApplyFilters={onFiltersChange}
        selectedExperienceTypes={selectedExperienceTypes}
        onExperienceTypeToggle={onExperienceTypeToggle}
      /></Suspense>}

      {/* Location Modal */}
      {showLocationModal && <Suspense fallback={null}><LocationSelectionModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        selectedLocation={selectedLocation}
        onLocationSelect={(location) => {
          if (location === "current") {
            if (navigator.geolocation) {
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  onLocationChange(
                    "Current Location",
                    position.coords.latitude,
                    position.coords.longitude
                  );
                  setShowLocationModal(false);
                },
                () => {
                  onLocationChange("Current Location");
                  setShowLocationModal(false);
                }
              );
            } else {
              onLocationChange("Current Location");
              setShowLocationModal(false);
            }
          } else {
            // If the picked location is an Eatzy saved address, parse and forward its numeric id
            // so the rest of the app can keep referring to "this saved address" (not just a name match).
            const savedAddressMatch =
              typeof location.id === "string"
                ? location.id.match(/^eatzy-address-(\d+)$/)
                : null;
            const savedAddressId = savedAddressMatch
              ? parseInt(savedAddressMatch[1], 10)
              : undefined;
            onLocationChange(
              location.name,
              location.coordinates?.lat,
              location.coordinates?.lng,
              savedAddressId
            );
            setShowLocationModal(false);
          }
        }}
      /></Suspense>}

      {/* Distance Selector Sheet */}
      {showDistanceSheet && <Suspense fallback={null}><DistanceSelectorSheet
        isOpen={showDistanceSheet}
        onClose={() => setShowDistanceSheet(false)}
        currentDistance={selectedDistance}
        onDistanceChange={onDistanceChange}
      /></Suspense>}
    </div>
  );
}
